import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
    GitMerge, MessageSquare, Rocket, ArrowRight, Sparkles,
    Users, Target, Zap, ChevronRight, Activity,
} from "lucide-react";
import SetupChecklistWidget from "@/components/dashboard/SetupChecklistWidget";

/**
 * HomePage — smart, action-first landing page after login.
 *
 * The 4 pillar action-cards are re-ordered based on the workspace's recent
 * activity (last 7 days, with 2× boost for the last 24h). Each card also
 * shows a live, translated hint so the operator sees the concrete next
 * step at a glance.
 */
export default function HomePage() {
    const { t } = useTranslation();
    const { user, workspace } = useAuth();
    const [reco, setReco] = useState(null);

    useEffect(() => {
        api.get("/home/recommendations").then((r) => setReco(r.data)).catch(() => {});
    }, [workspace?.id]);

    const first = user?.name?.split(" ")[0] || "operator";

    // Full action-card catalog, keyed by pillar id. The order is derived from
    // the /home/recommendations response — the card with the highest score
    // gets slot 0 (top-left) and a "trending" pill.
    const CARDS = useMemo(() => ({
        sales: {
            id: "sales",
            title: t("home.cards.sales_title"),
            body: t("home.cards.sales_body"),
            icon: Target, accent: "var(--jr-brand)",
            testid: "home-action-sales", cta: t("home.cards.sales_cta"), to: "/app/sales/signals",
            secondaryLinks: [
                { label: t("nav.sales_pipeline"), to: "/app/sales/pipeline" },
                { label: t("home.links.sales_campaigns"), to: "/app/sales/campaigns" },
                { label: t("nav.sales_outreach"), to: "/app/sales/outreach" },
            ],
        },
        recruit: {
            id: "recruit",
            title: t("home.cards.recruit_title"),
            body: t("home.cards.recruit_body"),
            icon: Users, accent: "var(--jr-amber)",
            testid: "home-action-recruit", cta: t("home.cards.recruit_cta"), to: "/app/recruit/search",
            secondaryLinks: [
                { label: t("nav.talent_pool"), to: "/app/recruit/pool" },
                { label: t("nav.candidate_pipeline"), to: "/app/recruit/pipeline" },
                { label: t("nav.candidate_outreach"), to: "/app/recruit/outreach" },
            ],
        },
        match: {
            id: "match",
            title: t("home.cards.match_title"),
            body: t("home.cards.match_body"),
            icon: GitMerge, accent: "var(--jr-info)",
            testid: "home-action-match", cta: t("home.cards.match_cta"), to: "/app/matches",
            secondaryLinks: [
                { label: t("home.links.network"), to: "/app/network" },
                { label: t("home.links.agents_console"), to: "/app/agents" },
            ],
        },
        chat: {
            id: "chat",
            title: t("home.cards.chat_title"),
            body: t("home.cards.chat_body"),
            icon: MessageSquare, accent: "#a855f7",
            testid: "home-action-chat", cta: t("home.cards.chat_cta"), to: "/app/chat/master_sales_agent",
            secondaryLinks: [
                { label: t("home.links.master_recruiter"), to: "/app/chat/master_recruiter_agent" },
                { label: t("nav.agent_memory"), to: "/app/agent-memory" },
            ],
        },
    }), [t]);

    // Translate a pillar hint coming from the backend (hint_key + hint_n).
    const hintFor = (p) => {
        if (!p?.hint_key) return p?.hint || "";
        return t(`home.hints.${p.hint_key}`, { n: p.hint_n });
    };

    // Ordered list (smart) — falls back to catalog order if reco not loaded yet.
    const ordered = useMemo(() => {
        if (!reco?.pillars?.length) {
            return ["sales", "recruit", "match", "chat"].map((id) => CARDS[id]);
        }
        return reco.pillars.map((p) => ({
            ...CARDS[p.id],
            score: p.score,
            hint: hintFor(p),
            recent: !!p.recent_action_at,
        }));
    }, [reco, CARDS]); // eslint-disable-line react-hooks/exhaustive-deps

    const totals = reco?.totals || {};
    const active24h = !!reco?.activity_last_24h;
    const topPillar = ordered[0];

    return (
        <div className="min-h-screen">
            <div className="px-8 pt-10 pb-8 border-b border-white/8">
                <div className="max-w-6xl">
                    <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-[var(--jr-text-3)] font-mono">
                        <span>{workspace?.name || "Workspace"} · Orchestrator</span>
                        {active24h && (
                            <span data-testid="home-active-pill"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] normal-case tracking-wider">
                                <span className="w-1.5 h-1.5 rounded-full bg-[var(--jr-brand)] animate-pulse"/>
                                {t("home.active_pill")}
                            </span>
                        )}
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2 font-semibold" data-testid="home-greeting">
                        {t("home.greeting_pre")} <span className="text-[var(--jr-brand)]">{first}</span>{t("home.greeting_post")}
                    </h1>
                    {reco && topPillar?.hint && (
                        <p className="mt-3 text-[var(--jr-text-2)] max-w-2xl leading-relaxed" data-testid="home-top-hint">
                            {t("home.top_hint_1")} <span className="text-white font-medium">{topPillar.title.toLowerCase()}</span> {t("home.top_hint_2")} {topPillar.hint.toLowerCase()}.
                        </p>
                    )}
                    <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-[var(--jr-text-2)]">
                        <Stat label={t("home.stats.signals")} v={totals.signals_open ?? "—"} />
                        <Stat label={t("home.stats.leads_week")} v={totals.leads_new_week ?? "—"} />
                        <Stat label={t("home.stats.pool")} v={totals.candidates_total ?? "—"} />
                        <Stat label={t("home.stats.matches")} v={totals.matches_open ?? "—"} />
                    </div>
                </div>
            </div>

            <div className="px-8 py-8 space-y-6 max-w-6xl">
                <SetupChecklistWidget />

                <div className="grid md:grid-cols-2 gap-4" data-testid="home-cards">
                    {ordered.map((a, i) => <ActionCard key={a.id} a={a} isTop={i === 0 && !!reco} t={t}/>) }
                </div>

                <div className="pt-6 mt-4 border-t border-white/8 flex flex-wrap items-center gap-4">
                    <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">
                        {t("home.also_available")}
                    </span>
                    <Link to="/app/dashboard" data-testid="home-to-dashboard"
                        className="inline-flex items-center gap-1.5 text-sm text-[var(--jr-text-2)] hover:text-white transition-colors">
                        <Zap size={13}/> {t("nav.dashboard")} <ChevronRight size={12}/>
                    </Link>
                    <Link to="/app/audit"
                        className="inline-flex items-center gap-1.5 text-sm text-[var(--jr-text-2)] hover:text-white transition-colors">
                        <Sparkles size={13}/> {t("nav.audit")} <ChevronRight size={12}/>
                    </Link>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, v }) {
    return (
        <div className="inline-flex items-baseline gap-1.5">
            <span className="font-display text-lg tracking-tight text-white">{v}</span>
            <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">{label}</span>
        </div>
    );
}

function ActionCard({ a, isTop, t }) {
    const Icon = a.icon;
    return (
        <div className={`group rounded-2xl border p-6 transition-all ${isTop
            ? "border-[var(--jr-brand)]/40 bg-gradient-to-br from-[var(--jr-brand)]/8 to-transparent shadow-[0_0_0_1px_rgba(255,140,42,0.12)]"
            : "border-white/8 bg-[var(--jr-surface)] hover:border-white/16"}`}
            data-testid={a.testid}>
            <div className="flex items-start justify-between gap-4">
                <div className="w-12 h-12 rounded-xl grid place-items-center shrink-0" style={{ background: `${a.accent}22`, color: a.accent }}>
                    <Icon size={22}/>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {isTop && (
                        <span data-testid={`${a.testid}-trending`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-mono uppercase tracking-[0.18em] bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border border-[var(--jr-brand)]/25">
                            <Activity size={9}/> {t("home.trending")}
                        </span>
                    )}
                    <Link to={a.to} data-testid={`${a.testid}-primary`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium hover:opacity-90 transition-opacity"
                        style={{ background: a.accent, color: "#000" }}>
                        {a.cta} <ArrowRight size={12}/>
                    </Link>
                </div>
            </div>
            <h3 className="font-display text-xl tracking-tight mt-4">{a.title}</h3>
            <p className="text-sm text-[var(--jr-text-2)] mt-1 leading-relaxed">{a.body}</p>
            {a.hint && (
                <div data-testid={`${a.testid}-hint`}
                    className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/8 text-[11px] text-[var(--jr-text-2)]">
                    <Sparkles size={10} className="text-[var(--jr-brand)]"/> {a.hint}
                </div>
            )}
            {a.secondaryLinks?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/6 flex flex-wrap gap-x-4 gap-y-1">
                    {a.secondaryLinks.map((l) => (
                        <Link key={l.to} to={l.to}
                            className="text-xs text-[var(--jr-text-3)] hover:text-white transition-colors inline-flex items-center gap-0.5">
                            {l.label} <ChevronRight size={10}/>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
